// PRE RMA CSB — MODULE DE CONFIGURATION MÉTIER UNIQUE.
//
// Toute règle chiffrée ou toute liste de valeurs (âge, tranches, CRENAS,
// diagnostics, traitements, TDR, Tableau de suivi) vit ICI et nulle part
// ailleurs dans le code. age.ts / tranches.ts / validation.ts / rma.ts ne
// contiennent plus aucune valeur "en dur" : ils lisent RULES et appliquent
// la logique — de sorte qu'une évolution future d'une règle (déplacer un
// seuil, ajouter un diagnostic, changer un mot-clé CRENAS) se fait à UN
// SEUL endroit, sans toucher au reste de l'application (backend et
// frontend importent tous les deux depuis shared/business-logic).
//
// ⚠️ Toute modification de ce fichier change le résultat des calculs RMA.
// Documenter le changement dans docs/DECISIONS.md avant de le déployer, et
// relancer la suite de tests (shared/business-logic/__tests__) qui
// vérifie ces valeurs one par one, dont le test de non-régression face au
// fichier Pre_RMA_V12.xlsx original.
//
// Ce fichier a été extrait tel quel des valeurs déjà en usage (Phase 1-3) —
// aucune règle n'a été modifiée en le créant, seulement déplacée ici.

export type Genre = 'M' | 'F';
export type AgeUnite = 'jours' | 'mois' | 'ans';
export type TdrResultat = '100' | '101' | '110' | '111';

export interface DiagnosticRef {
  code: string;
  libelle: string;
  ageMinJours: number | null;
  ageMaxJours: number | null;
  genreRequis: Genre | null;
  /** Affiché par défaut à la saisie (diagnostics principaux). Les autres sont masqués derrière « + ». */
  courant?: boolean;
  /** Ancien code conservé pour les consultations déjà saisies ; jamais proposé à la saisie. */
  legacy?: boolean;
}

/** Une tranche = "valide jusqu'à `jusquaJours` inclus" ; Infinity = "et plus". */
export interface TrancheBorne {
  jusquaJours: number;
  label: string;
}

export interface TrancheCsajBorne {
  minJoursInclus: number;
  maxJoursExclu: number;
  label: '10-14' | '15-19' | '20-24';
}

/** Une tranche = "valide jusqu'à `jusquaAns` inclus" ; Infinity = "et plus". */
export interface TrancheAgeCpn {
  jusquaAns: number;
  label: string;
}

export const RULES = {
  /** Règles d'âge (Phase1 §3.2 et §3.5 — décision validée : HTA = 18 ans). */
  age: {
    // Bornes de saisie strictes (colonne W de Saisie_Unique) : une seule
    // unité possible par tranche d'âge réelle.
    bornesParUnite: {
      jours: { min: 0, max: 59 },
      mois: { min: 2, max: 59 },
      ans: { min: 5, max: Infinity },
    } satisfies Record<AgeUnite, { min: number; max: number }>,
    joursParUnite: { jours: 1, mois: 30, ans: 365 } satisfies Record<AgeUnite, number>,
    seuils: { unAn: 365, cinqAns: 1825, dixHuitAns: 6570 },
  },

  /** TDR : seuls 101/110/111 sont positifs (100 = négatif). */
  tdr: {
    positifs: ['101', '110', '111'] as TdrResultat[],
  },

  /**
   * Diagnostics/Symptômes — LISTE OFFICIELLE du RMA (Tableau 4, RMA_CSB_Complet.xlsx, lignes 6 à 80), dans l'ordre et avec
   * les libellés exacts du modèle (la ligne 34 « traitement de pré-référence » est un comptage, pas un diagnostic).
   * `courant: true` = diagnostics directement affichés à la saisie ; les autres sont masqués derrière le bouton « + ».
   * (Anciennement : équivalent de la feuille Liste!A2:A32.)
   * Pour AJOUTER un diagnostic (équivalent Liste!A33:A35) : ajouter une
   * entrée ici avec un `code` unique — il apparaît alors automatiquement
   * en Saisie Unique ET dans les calculs RMA (source unique de vérité,
   * point 14 du cahier des charges), sans toucher à aucun autre fichier.
   */
  diagnostics: [
    { code: 'SIGNE_IBG', libelle: 'Signes d’Infections Bactériennes Graves', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'PNEUMONIE_SEVERE', libelle: 'Pneumonie sévère', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'DYSENTERIE', libelle: 'Dysenterie/Diarrhée sanguinolente', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'SUSP_TYPHOIDE', libelle: 'Suspicion de Fièvre Typhoïde', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'DIARRHEE_AVEC_DH2O', libelle: 'Diarrhées avec déshydratation', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'DIARRHEE_SANS_DH2O', libelle: 'Diarrhées sans déshydratation', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'SUSP_CHOLERA', libelle: 'Suspicion de Cholera', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SD_GRIPPAL', libelle: 'Syndrome grippal', ageMinJours: 1825, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'VARICELLE', libelle: 'Varicelle', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'OREILLON', libelle: 'Oreillon', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'IRA', libelle: 'IRA (Toux avec Rhume ou Maux de gorge ou Otite et Fièvre apparue depuis moins de 15 jours)', ageMinJours: 1825, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'PNEUMONIE', libelle: 'Pneumonie', ageMinJours: null, ageMaxJours: 1824, genreRequis: null, courant: true },
    { code: 'TOUX_RHUME', libelle: 'Toux ou Rhume', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'ASTHME', libelle: 'Asthme', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'MAUX_GORGE', libelle: 'Maux de gorge', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'MAPI_GRAVE', libelle: 'MAPI grave', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'MAPI_MINEUR', libelle: 'MAPI mineur', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_ROUGEOLE_RUBEOLE', libelle: 'Suspicion de rougeole/rubéole', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'DIPHTERIE', libelle: 'Diphtérie', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'COQUELUCHE', libelle: 'Coqueluche', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'TETANOS', libelle: 'Tétanos', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'PFA', libelle: 'Paralysie Flasque Aigue', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'ECOULEMENT_GENITAL', libelle: 'Écoulement génital', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'ULCERATION_GENITAL', libelle: 'Ulcération génitale', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'INFECTION_VIH', libelle: 'Infection à VIH', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'PALUDISME_SIMPLE', libelle: 'Paludisme simple', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'PALUDISME_FE', libelle: 'Paludisme chez les femmes enceintes', ageMinJours: 1825, ageMaxJours: null, genreRequis: 'F', courant: true },
    { code: 'PALUDISME_GRAVE', libelle: 'Paludisme grave autre que FE', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'TOUX_CHRONIQUE', libelle: 'Toux plus 3 semaines Suspicion de Tuberculose', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'TB_CONFIRMEE', libelle: 'Tuberculose confirmée Bascilloscopie (CDT CSB)', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'LEPRE_PB', libelle: 'Lèpre Pauci Bacillaire (PB)', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'LEPRE_MB', libelle: 'Lèpre Multi Bacillaire (MB)', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'ANEMIE', libelle: 'Anémie', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'INSUFFISANCE_PONDERALE', libelle: 'Insuffisance pondérale', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'MAM', libelle: 'Malnutrition Aigüe Modérée', ageMinJours: null, ageMaxJours: 1824, genreRequis: null, courant: true },
    { code: 'MAS', libelle: 'Malnutrition Aigüe Sévère', ageMinJours: null, ageMaxJours: 1824, genreRequis: null, courant: true },
    { code: 'SURPOIDS_OBESITE', libelle: 'Surpoids / Obésité', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'RETARD_CROISSANCE', libelle: 'Retard de croissance', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'OTITE', libelle: 'Otite', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'DEFICIENCE_AUDITIVE', libelle: 'Déficience auditive', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'OEIL_ROUGE_TRAUMA', libelle: 'Œil rouge traumatique', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'CONJONCTIVITE', libelle: 'Conjonctivite', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'DEFICIENCE_VISUELLE', libelle: 'Déficience visuelle', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_CATARACTE', libelle: 'Suspicion de cataracte', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'CARRIE_DENTAIRE', libelle: 'Caries dentaires', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'EPILEPSIE', libelle: 'Épilepsie', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'TROUBLES_MENTAUX', libelle: 'Troubles mentaux', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'HTA', libelle: 'HTA', ageMinJours: 6570, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'AVC', libelle: 'Accident Vasculaire Cérébral', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_NEUROCYSTICERCOSE', libelle: 'Suspicion de neurocysticercose', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_DIABETE', libelle: 'Suspicion de diabète', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'DIABETE_CONFIRME', libelle: 'Diabète confirmé', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_DREPANOCYTOSE', libelle: 'Suspicion de Drépanocytose', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'HYDROCELE', libelle: 'Hydrocèle', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'LYMPHOEDEME', libelle: 'Lymphœdème', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'PUCES_CHIQUES', libelle: 'Incrustation des puces chiques dans la peau', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'DOULEUR_ARTICULAIRE', libelle: 'Douleurs articulaires', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'IMC', libelle: 'Infirmité Motrice Cérébrale', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'TROUBLES_URINAIRES', libelle: 'Troubles urinaires', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'HEMATURIE', libelle: 'Hématurie', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'FISTULES_OBSTETRICALES', libelle: 'Fistules obstétricales', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_PESTE_PULMONAIRE', libelle: 'Suspicion de cas de Peste pulmonaire', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_PESTE_BUBONIQUE', libelle: 'Suspicion de cas de peste bubonique', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'ICTERE', libelle: 'Ictère', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'AUTRES', libelle: 'Autres nouveaux cas de maladies non épidémiques à préciser dans l’observation', ageMinJours: null, ageMaxJours: null, genreRequis: null, courant: true },
    { code: 'EVENEMENTS_EPIDEMIQUES', libelle: 'Évènements ou autre maladies épidémiques inattendus', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_MENINGITE', libelle: 'Suspicion de méningite', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_ARBOVIROSES', libelle: 'Suspicion d’Arboviroses (Dengue, Chikungunya)', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_FVR', libelle: 'Suspicion de Fièvre de la Vallée de Rift', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'AUTRES_FIEVRES_HEMORRAGIQUES', libelle: 'Autres Fièvres hémorragiques', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'TIAC', libelle: 'Toxi -Infection Alimentaire Collective', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'INTOXICATION_ANIMAUX_MARINS', libelle: 'Intoxication par consommation d’Animaux Marins', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'MORSURES_GRIFFURES', libelle: 'Morsures/griffures par animal à sang chaud', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    { code: 'SUSP_RAGE', libelle: 'Suspicion de rage humaine', ageMinJours: null, ageMaxJours: null, genreRequis: null },
    // Ancienne saisie (« Lèpre MB/PB » regroupée) : conservée UNIQUEMENT pour relire/compter les consultations déjà enregistrées.
    // Non proposée à la saisie (le RMA officiel a deux lignes distinctes : PB et MB).
    { code: 'LEPRE_MB_PB', libelle: 'Lèpre MB/PB (ancienne saisie)', ageMinJours: null, ageMaxJours: null, genreRequis: null, legacy: true },
  ] as DiagnosticRef[],

  diagnosticsPaludisme: ['PALUDISME_SIMPLE', 'PALUDISME_GRAVE', 'PALUDISME_FE'],

  /** Traitement autorisé par diagnostic paludisme (Phase1 §3.4, règles AB.17-19). */
  traitementAutorise: {
    PALUDISME_SIMPLE: ['ACT'],
    PALUDISME_GRAVE: ['ACT, Artesunate injectable'],
    PALUDISME_FE: ['ACT', 'ACT, Artesunate injectable'],
  } as Record<string, string[]>,

  /** Mots-clés de recherche large pour le comptage RMA Partie 3 (indépendant du diagnostic précis). */
  traitement: {
    motCleAct: 'ACT',
    motCleArtesunate: 'Artesunate injectable',
  },

  /**
   * Les 4 découpages de tranches d'âge (Phase1 §3.3) — INDÉPENDANTS,
   * ne jamais les fusionner. Chaque tableau est parcouru dans l'ordre ;
   * la première borne où `ageJours <= jusquaJours` s'applique.
   */
  tranches: {
    morbidite: [
      { jusquaJours: 28, label: '0-28j' },
      { jusquaJours: 59, label: '29-59j' },
      { jusquaJours: 359, label: '2-11mois' },
      { jusquaJours: 1824, label: '1-4ans' },
      { jusquaJours: 5110, label: '5-14ans' },
      { jusquaJours: 8760, label: '15-24ans' },
      { jusquaJours: 21535, label: '25-59ans' },
      { jusquaJours: Infinity, label: '60ans+' },
    ] as TrancheBorne[],

    traumatisme: [
      { jusquaJours: 1824, label: '0-4ans' },
      { jusquaJours: 3285, label: '5-9ans' },
      { jusquaJours: 5110, label: '10-14ans' },
      { jusquaJours: 6205, label: '15-17ans' },
      { jusquaJours: 8760, label: '18-24ans' },
      { jusquaJours: 21535, label: '25-59ans' },
      { jusquaJours: Infinity, label: '60ans+' },
    ] as TrancheBorne[],

    // Paludisme (RMA Partie 3 / Tableau 8) : 5 tranches, dans l'ordre des lignes 7 à 11 du modèle officiel.
    // 1825 j = 5 ans (inclus dans « 1-5 ans »), 4745 j = 13 ans, 8760 j = 24 ans (365 j par an, comme parseAge).
    palu: [
      { jusquaJours: 359, label: '0-11 mois' },
      { jusquaJours: 1825, label: '1-5 ans' },
      { jusquaJours: 4745, label: '6-13 ans' },
      { jusquaJours: 8760, label: '14-24 ans' },
      { jusquaJours: Infinity, label: '25 ans et plus' },
    ] as TrancheBorne[],

    nutrition: {
      // Vide (aucune tranche) à partir de ce seuil — colonne R de Saisie_Unique.
      seuilExclusionJours: 1825,
      bornes: [
        { jusquaJours: 179, label: '0-5 mois' },
        { jusquaJours: 359, label: '6-11 mois' },
        { jusquaJours: 719, label: '12-23 mois' },
        { jusquaJours: Infinity, label: '24-59 mois' },
      ] as TrancheBorne[],
    },

    csaj: [
      { minJoursInclus: 3650, maxJoursExclu: 5475, label: '10-14' },
      { minJoursInclus: 5475, maxJoursExclu: 7300, label: '15-19' },
      { minJoursInclus: 7300, maxJoursExclu: 9125, label: '20-24' },
    ] as TrancheCsajBorne[],
  },

  /** Catégories de traumatismes/violences — RMA Partie 2 (Liste!B2:B5, hors "Aucun"). */
  traumatismes: {
    categories: ['VIOLENCE_SEXUELLE', 'AUTRE_VIOLENCE', 'AUTRE_TRAUMATISME'],
  },

  /** Indicateurs et admissions CRENAS (Liste!F2:G7). */
  crenas: {
    libelles: { mamPT: 'MAM P/T', mamPB: 'MAM PB', masPT: 'MAS P/T', masPB: 'MAS PB' },
    indicateursMam: ['MAM P/T', 'MAM PB'],
    indicateursMas: ['MAS P/T', 'MAS PB', 'Œdème', 'Oedeme'],
    // Mots-clés de recherche large utilisés en RMA Partie 4 (dépistage, pas
    // liés à un diagnostic MAM/MAS précis) :
    motClePb: 'PB',
    motClesOedeme: ['Œdème', 'Oedeme'],
    admissions: ['Admis (NA M)', 'Admis (NA F)', 'Admis (Rechute M)', 'Admis (Rechute F)', 'Guéri', 'Sortie autre'],
    admissionsParGenre: {
      M: ['Admis (NA M)', 'Admis (Rechute M)'],
      F: ['Admis (NA F)', 'Admis (Rechute F)'],
    } satisfies Record<Genre, string[]>,
    prefixeAdmis: 'Admis',
    libelleGueri: 'Guéri',
    libelleSortieAutre: 'Sortie autre',
  },

  /** Partie 6 CSAJ : IST = Ulcération OU Écoulement (point 24), dédoublonné par patient. */
  csaj: {
    diagnosticsIst: ['ULCERATION_GENITAL', 'ECOULEMENT_GENITAL'],
  },

  /** Tableau de suivi : répartition <5ans / ≥5ans (seuil 1825j = colonne N). */
  tableauSuivi: {
    tranchesMoins5ans: ['0-28j', '29-59j', '2-11mois', '1-4ans'],
    tranches5ansEtPlus: ['5-14ans', '15-24ans', '25-59ans', '60ans+'],
  },

  /**
   * Module CPN (Consultation Prénatale) — indépendant du RCE, ajouté après
   * les Phases 1-8. Source : fiche RMA CPN (Tableau 12) fournie par le
   * client + guide officiel de remplissage du registre CPN. Contrairement
   * au RCE, il n'existe pas de fichier Excel de référence pour ce module :
   * les bornes ci-dessous sont donc reprises telles que validées par le
   * client sur l'outil de saisie autonome (PRE_RMA_CPN.html) qui a précédé
   * cette intégration, pas d'un tableur préexistant.
   */
  cpn: {
    ageBornes: { min: 10, max: 99 },
    // Tranches CPN1 (Phase1-CPN équivalent) : "valide jusqu'à jusquaAns inclus".
    tranchesCpn1: [
      { jusquaAns: 14, label: '<15 ans' },
      { jusquaAns: 17, label: '15-17 ans' },
      { jusquaAns: Infinity, label: '18 ans et plus' },
    ] satisfies TrancheAgeCpn[],
  },

  /**
   * Historique des écarts volontaires par rapport au fichier Excel original
   * — voir docs/DECISIONS.md pour le détail complet de chacun. Gardé ici
   * à titre de repère rapide pour qui modifie ce fichier : ces 3 points
   * sont déjà "la bonne valeur" ci-dessus, ne pas les faire régresser.
   */
  corrections: [
    { id: 1, resume: 'HTA = 18 ans (6570j), et non 5 ans comme indiqué dans le cahier des charges initial.' },
    { id: 2, resume: "RMA Partie 1, ligne Cas de fièvre, tranche 2-11mois M : formule manquante dans l'Excel original, calculée symétriquement ici." },
    { id: 3, resume: "Tableau de suivi Admis/Guérie : lisent l'admission CRENAS (pas la colonne Traumatisme, référence erronée dans l'Excel original)." },
  ],
} as const;
